/**
 * The canvas. Drives xyflow from `DefinitionState`:
 *
 * - Nodes are laid out with dagre on every render (deterministic). There is
 *   no manual positioning: the graph is always a function of the definition.
 * - Fan-out groups render as dotted container nodes behind their members.
 * - Edges are hidden by default (per the design principles); the "Show
 *   connections" toggle in the top bar reveals typed ports + edge lines.
 *
 * The canvas owns the context-aware add affordances:
 *   - Append-after: "+"  on the right of each node → NodePicker
 *   - Insert-between: "+" on each edge midpoint → NodePicker (auto re-wires)
 *   - Add-parallel: button on each node → NodePicker (shares upstream)
 *   - Library click: from the left rail — appends after the selected node,
 *     or inserts as a new root if nothing is selected.
 *
 * Drag-and-drop is intentionally disabled. All composition happens through
 * explicit buttons so the result is always tidy and predictable.
 */

import React from 'react';
import clsx from 'clsx';
import {
  Background,
  BackgroundVariant,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  type Edge as RFEdge,
  type Node as RFNode,
  useReactFlow
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { PlusIcon } from '@heroicons/react/24/outline';

import { useBuilder } from '~/pages/workflows/builder/state/context';
import { createNode } from '~/pages/workflows/builder/state/reducer';
import {
  bestOutputMatch,
  computeEdges,
  computeFanOutGroups,
  computeLayout,
  effectiveOutputs,
  estimateNodeHeight,
  groupForNode,
  isEnumerableNode,
  NODE_WIDTH,
  primaryDataInput
} from '~/pages/workflows/builder/state/selectors';
import { validate, issuesForNode } from '~/pages/workflows/builder/state/validation';

import { BindingEdge } from './binding-edge';
import { FanOutGroupNode } from './fan-out-group';
import { NodePicker, type NodePickerResult } from './node-picker';
import { TaskNode, type TaskNodeData } from './task-node';
import type { InputSubtype, TaskDefinitionKey } from '~/pages/workflows/builder/types';

const NODE_TYPES = {
  task: TaskNode,
  fanOutGroup: FanOutGroupNode
};

const EDGE_TYPES = {
  binding: BindingEdge
};

interface PickerState {
  open: boolean;
  mode: 'append-after' | 'insert-between' | 'add-parallel' | 'new-node' | 'add-input';
  anchor?: { nodeId: string; fromOutputKey?: string; toNodeId?: string; toInputKey?: string };
  title?: string;
  subtitle?: string;
}

export function Canvas() {
  return (
    <ReactFlowProvider>
      <CanvasInner />
    </ReactFlowProvider>
  );
}

function CanvasInner() {
  const { state, dispatch, selectedNodeId, setSelectedNodeId } = useBuilder();
  const rf = useReactFlow();
  const [picker, setPicker] = React.useState<PickerState>({ open: false, mode: 'new-node' });

  const issues = React.useMemo(() => validate(state), [state]);
  const layout = React.useMemo(() => computeLayout(state), [state]);
  const fanOutGroups = React.useMemo(() => computeFanOutGroups(state), [state]);
  const edges = React.useMemo(() => computeEdges(state), [state]);

  // Refit the viewport whenever the graph's structure changes so newly-added
  // nodes are always in view without the user chasing them around.
  const structuralKey = React.useMemo(
    () => `${state.nodes.map((n) => n.id).join(',')}|${edges.map((e) => e.id).join(',')}`,
    [state.nodes, edges]
  );
  React.useEffect(() => {
    const t = setTimeout(() => {
      rf.fitView({ padding: 0.2, maxZoom: 1, duration: 250 });
    }, 30);
    return () => clearTimeout(t);
  }, [structuralKey, rf]);

  // ----- Canvas actions -----

  const addNewNode = (taskKey: TaskDefinitionKey, subtype?: InputSubtype) => {
    const base = createNode(taskKey, { inputSubtype: subtype });
    dispatch({ type: 'add-node', node: base });
    if (subtype) {
      dispatch({ type: 'set-input-subtype', nodeId: base.id, subtype });
    }
    setSelectedNodeId(base.id);
    return base.id;
  };

  const appendAfter = (sourceNodeId: string, result: NodePickerResult) => {
    const sourceNode = state.nodes.find((n) => n.id === sourceNodeId);
    if (!sourceNode) return;
    const newNode = createNode(result.taskKey, { inputSubtype: result.inputSubtype });
    const dataField = primaryDataInput(result.taskKey);
    if (dataField) {
      const output = bestOutputMatch(sourceNode, dataField);
      if (output) {
        newNode.bindings.push({
          inputKey: dataField.key,
          source: {
            type: 'TASK_OUTPUT',
            sourceNodeId: sourceNode.id,
            sourceOutputKey: output.key
          }
        });
      }
    }
    dispatch({ type: 'add-node', node: newNode });
    if (result.inputSubtype) {
      dispatch({ type: 'set-input-subtype', nodeId: newNode.id, subtype: result.inputSubtype });
    }
    setSelectedNodeId(newNode.id);
  };

  const insertBetween = (
    fromNodeId: string,
    toNodeId: string,
    toInputKey: string,
    result: NodePickerResult
  ) => {
    const fromNode = state.nodes.find((n) => n.id === fromNodeId);
    if (!fromNode) return;
    const dataField = primaryDataInput(result.taskKey);
    if (!dataField) {
      appendAfter(fromNodeId, result);
      return;
    }
    const upstreamOutput = bestOutputMatch(fromNode, dataField);
    if (!upstreamOutput) {
      appendAfter(fromNodeId, result);
      return;
    }
    const newNode = createNode(result.taskKey, { inputSubtype: result.inputSubtype });
    // Pick a compatible output on the new node for the downstream.
    const newOutputs = effectiveOutputs(newNode);
    const downstreamOutput = newOutputs[0];
    if (!downstreamOutput) {
      appendAfter(fromNodeId, result);
      return;
    }
    dispatch({
      type: 'insert-between',
      fromNodeId,
      toNodeId,
      downstreamInputKey: toInputKey,
      newNode,
      newNodeInputKey: dataField.key,
      newNodeOutputKey: downstreamOutput.key,
      fromOutputKey: upstreamOutput.key
    });
    if (result.inputSubtype) {
      dispatch({ type: 'set-input-subtype', nodeId: newNode.id, subtype: result.inputSubtype });
    }
    setSelectedNodeId(newNode.id);
  };

  const addParallel = (siblingNodeId: string, result: NodePickerResult) => {
    const newNode = createNode(result.taskKey, { inputSubtype: result.inputSubtype });
    dispatch({ type: 'add-parallel', siblingNodeId, newNode });
    if (result.inputSubtype) {
      dispatch({ type: 'set-input-subtype', nodeId: newNode.id, subtype: result.inputSubtype });
    }
    setSelectedNodeId(newNode.id);
  };

  // ----- Picker handlers -----

  const onPickerResult = (result: NodePickerResult) => {
    const anchor = picker.anchor;
    setPicker({ open: false, mode: picker.mode });
    if (picker.mode === 'append-after' && anchor) {
      appendAfter(anchor.nodeId, result);
    } else if (picker.mode === 'insert-between' && anchor && anchor.toNodeId && anchor.toInputKey) {
      insertBetween(anchor.nodeId, anchor.toNodeId, anchor.toInputKey, result);
    } else if (picker.mode === 'add-parallel' && anchor) {
      addParallel(anchor.nodeId, result);
    } else {
      addNewNode(result.taskKey, result.inputSubtype);
    }
  };

  // ----- Build xyflow nodes / edges -----

  const rfNodes: RFNode[] = React.useMemo(() => {
    const nodes: RFNode[] = [];

    // Group container nodes (drawn behind actual task nodes). We size each
    // member's bounding box using its estimated height so the group box hugs
    // parallel siblings of different sizes correctly.
    const nodeById = new Map(state.nodes.map((n) => [n.id, n] as const));
    for (const group of fanOutGroups) {
      const memberBoxes = group.memberNodeIds
        .map((id) => {
          const pos = layout.get(id);
          const n = nodeById.get(id);
          if (!pos || !n) return null;
          return { x: pos.x, y: pos.y, h: estimateNodeHeight(n) };
        })
        .filter((p): p is { x: number; y: number; h: number } => !!p);
      if (memberBoxes.length === 0) continue;
      const minX = Math.min(...memberBoxes.map((p) => p.x)) - 24;
      const minY = Math.min(...memberBoxes.map((p) => p.y)) - 32;
      const maxX = Math.max(...memberBoxes.map((p) => p.x + NODE_WIDTH)) + 24;
      const maxY = Math.max(...memberBoxes.map((p) => p.y + p.h)) + 24;
      nodes.push({
        id: `group_${group.id}`,
        type: 'fanOutGroup',
        position: { x: minX, y: minY },
        draggable: false,
        selectable: false,
        data: { label: group.sourceLabel },
        style: { width: maxX - minX, height: maxY - minY, zIndex: -1 },
        zIndex: -1
      });
    }

    // Task nodes
    for (const node of state.nodes) {
      const position = layout.get(node.id) ?? { x: 0, y: 0 };
      const nodeIssues = issuesForNode(issues, node.id);
      const data: TaskNodeData = {
        node,
        issues: nodeIssues,
        isSelected: selectedNodeId === node.id,
        isEnumerable: isEnumerableNode(state, node),
        onAppendAfter: (nodeId) =>
          setPicker({
            open: true,
            mode: 'append-after',
            anchor: { nodeId },
            title: 'Add next step',
            subtitle: 'Choose what should come after this step.'
          }),
        onInsertParallel: (nodeId) =>
          setPicker({
            open: true,
            mode: 'add-parallel',
            anchor: { nodeId },
            title: 'Add parallel step',
            subtitle: 'Add a sibling that uses the same upstream data.'
          }),
        onRequestSubtype: (nodeId, subtype) => {
          dispatch({ type: 'set-input-subtype', nodeId, subtype });
        },
        onSelectNode: (nodeId) => setSelectedNodeId(nodeId),
        onDelete: (nodeId) => dispatch({ type: 'remove-node', nodeId })
      };
      nodes.push({
        id: node.id,
        type: 'task',
        position,
        data: data as unknown as Record<string, unknown>,
        draggable: false,
        selected: selectedNodeId === node.id,
        zIndex: 1
      });
    }
    return nodes;
  }, [state, layout, fanOutGroups, issues, selectedNodeId, dispatch, setSelectedNodeId]);

  const rfEdges: RFEdge[] = React.useMemo(() => {
    return edges
      .filter((e) => e.sourceNodeId !== null)
      .map((e) => ({
        id: e.id,
        source: e.sourceNodeId!,
        target: e.targetNodeId,
        sourceHandle: e.sourceOutputKey ?? undefined,
        type: 'binding',
        data: {
          forEach: e.forEach,
          onInsertBetween: () => {
            setPicker({
              open: true,
              mode: 'insert-between',
              anchor: {
                nodeId: e.sourceNodeId!,
                toNodeId: e.targetNodeId,
                toInputKey: e.targetInputKey,
                fromOutputKey: e.sourceOutputKey ?? undefined
              },
              title: 'Insert a step here',
              subtitle: 'The new step will take the upstream output and feed the downstream step.'
            });
          }
        }
      }));
  }, [edges]);

  return (
    <div className="relative h-full w-full">
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        onPaneClick={() => setSelectedNodeId(null)}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        panOnDrag
        elementsSelectable
        minZoom={0.25}
        maxZoom={1.5}
        className={clsx('bg-neutral-50 dark:bg-neutral-900')}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={16}
          size={1}
          className="!bg-neutral-50 dark:!bg-neutral-900"
        />
        <Controls showInteractive={false} position="bottom-right" className="!rounded-md" />
      </ReactFlow>

      {state.nodes.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="pointer-events-auto max-w-sm rounded-xl border border-dashed border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 p-6 text-center shadow-sm">
            <div className="text-sm font-semibold text-neutral-900 dark:text-white">
              Start with an Input
            </div>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Pick any step to begin. You can always change it later.
            </p>
            <button
              type="button"
              onClick={() =>
                setPicker({
                  open: true,
                  mode: 'new-node',
                  title: 'Add a step',
                  subtitle: 'Inputs feed the workflow. Analysis and outputs come next.'
                })
              }
              className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
            >
              <PlusIcon className="h-3.5 w-3.5" /> Add a step
            </button>
          </div>
        </div>
      )}

      <NodePicker
        open={picker.open}
        onClose={() => setPicker({ ...picker, open: false })}
        onPick={onPickerResult}
        title={picker.title}
        subtitle={picker.subtitle}
        showInputSubtypes={picker.mode !== 'insert-between' && picker.mode !== 'add-parallel'}
      />
    </div>
  );
}

// This is intentionally exported so builder.tsx can still reference `groupForNode`
// etc. for non-canvas summaries (e.g. inspector labels).
export { groupForNode };
